import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useToast } from '@morressier/alembic';
import { zodResolver } from '@hookform/resolvers/zod';
import { Overlay } from 'src/components/Overlay';
import useCurrentContextId from 'src/hooks/useCurrentContextId';
import { ICommandResponse, useInviteExperts } from 'src/api/commands';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import ConfirmInviteExitModal from '../../ReviewTab/InviteReviewer/ConfirmInviteExitModal';
import {
  emailContentSchema,
  inviteAdjudicatorsSchema
} from '@publishing-engine/domain/validation';
import { IEarlyAssessmentEditorialRequest } from '@publishing-engine/domain/models';
import AddContextToInvitation from '../InviteExperts/AddContextToInvitation';
import { IInviteAdjudicatorsForm } from '../InviteExperts/commonTypes';
import ConfirmInviteBoardMemberModal from '../InviteExperts/ConfirmInviteBoardMemberModal';
import AddAdjudicators from './AddAdjudicators';
import { ensureUser } from 'src/api/queries';
import { EmailContentForm } from '../../../EmailModule';
import { IEmailCommandPayload } from '@publishing-engine/domain/types/email-template';
import ComposeAdjudicatorInvitation from './ComposeAdjudicatorInvitation';
import { buildEmailPayload } from '../../../../utils/normalizeEmailRecipients';
import { useAttachmentSync } from 'src/hooks/useAttachmentSync';

interface Props {
  closeModal: () => void;
}

const steps = [
  AddAdjudicators,
  AddContextToInvitation,
  ComposeAdjudicatorInvitation
];

const InviteAdjudicatorsModal = ({ closeModal }: Props) => {
  // state
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showExitConfirmModal, setShowExitConfirmModal] =
    useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // hooks
  const toast = useToast();
  const submissionContextId = useCurrentContextId() ?? '';
  const { availableActions, submissionId, organizationId } =
    useSubmissionData();
  const totalSteps = steps.length;

  const adjudicatorsMethods = useForm<IInviteAdjudicatorsForm>({
    defaultValues: {
      experts: [
        {
          id: '',
          name: '',
          email: '',
          externalExpertEmail: ''
        }
      ],
      addContext: {
        attachments: [],
        comment: ''
      }
    },
    resolver: zodResolver(inviteAdjudicatorsSchema)
  });

  const {
    formState: { errors, isDirty },
    reset
  } = adjudicatorsMethods;

  const emailContentMethods = useForm<EmailContentForm>({
    defaultValues: {
      emailContent: {
        body: '',
        subject: '',
        recipients: { to: [], cc: [], bcc: [] },
        attachments: []
      }
    },
    resolver: zodResolver(emailContentSchema)
  });

  // This is specifically for bidirectional sync between the steps,
  // the initial data is passed as props to the component
  useAttachmentSync({
    sourceForm: adjudicatorsMethods,
    targetForm: emailContentMethods,
    sourceField: 'addContext.attachments',
    targetField: 'emailContent.attachments',
    transformToTarget: (files: any[]) => {
      if (!Array.isArray(files)) return [];
      return files.map(file => ({
        id: file.fileId || file.id,
        referenceName: file.referenceName,
        name: file.name,
        extension: file.extension,
        url: file.url
      }));
    },
    transformToSource: (files: any[]) => {
      if (!Array.isArray(files)) return [];
      return files.map(file => ({
        fileId: file.id,
        referenceName: file.referenceName,
        name: file.name,
        extension: file.extension,
        url: file.url,
        isUploading: false,
        progress: 0
      }));
    }
  });

  const Component = steps[currentStep - 1];

  if (!Component) return null;

  const goToNextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const goToPreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleCloseModal = () => {
    // show exit confirmation modal
    if (isDirty) {
      setShowExitConfirmModal(true);
    } else {
      reset();
      closeModal();
    }
  };

  const handleExit = () => {
    setShowExitConfirmModal(false);
    closeModal();
    reset();
  };
  const reviewListKey = [
    'adjudications-experts-invite-list',
    { submissionContextId, submissionId }
  ];

  const inviteReviewerMutation = useInviteExperts(
    submissionId,
    submissionContextId,
    reviewListKey,
    'RequestAdjudication',
    organizationId
  );

  const handleReviewerInvite = async (
    experts: { id: string; email: string }[],
    newExpertsAddToContext: IEarlyAssessmentEditorialRequest,
    externalExpertEmails: string[]
  ) => {
    const externalUsers = await Promise.all(
      externalExpertEmails.map(async email => {
        const user = await ensureUser(email);
        return { id: user.userId, email }; // preserve original email
      })
    );
    const results: ICommandResponse[] = [];
    const allExperts = [...experts, ...externalUsers];
    if (availableActions.contains('RequestAdjudication')) {
      const { getValues } = emailContentMethods;
      const baseEmailContext = getValues('emailContent');
      for (const { id, email } of allExperts) {
        const emails: IEmailCommandPayload = buildEmailPayload(
          baseEmailContext,
          [email]
        );
        results.push(
          await inviteReviewerMutation.mutateAsync({
            id,
            editorialRequest: newExpertsAddToContext,
            emails
          })
        );
      }
    }
    const successCount = results.filter(result => result.isSuccess).length;

    const rejectedCount = results.filter(result => !result.isSuccess).length;

    if (rejectedCount) {
      toast({
        variant: 'error',
        title: 'Invites Failed',
        description: `${rejectedCount} Adjudicators Failed to Invite, Please try again.`,
        isClosable: true
      });
    }

    return successCount;
  };
  const handleInviteExperts = async () => {
    if (errors.experts?.length || isProcessing) {
      return;
    }

    setIsProcessing(true);

    // @NOTE: This is a temporary solution to invite reviewers, until we work on add reviewer to queue
    // this will make sure that the reviewers are being invited using new UI
    const newExperts = adjudicatorsMethods.getValues('experts');
    const newExpertsAddToContext = adjudicatorsMethods.getValues('addContext');
    let noOfInvitedReviewers = 0;
    if (newExperts.length) {
      const inviteExperts = newExperts
        .map(expert => {
          return { id: expert.id?.trim(), email: expert.email };
        })
        .filter(
          (expert): expert is { id: string; email: string } => !!expert.id
        );
      const externalExpertEmails = newExperts
        .map(expert => expert.externalExpertEmail)
        .filter(
          (externalExpertEmail): externalExpertEmail is string =>
            !!externalExpertEmail?.trim()
        );
      noOfInvitedReviewers = await handleReviewerInvite(
        inviteExperts,
        newExpertsAddToContext,
        externalExpertEmails
      );
    }

    toast({
      variant: 'success',
      title: `${noOfInvitedReviewers} Adjudicators Invited.`,
      description:
        'Adjudicators will be emailed an invitation to support on this Submission.',
      isClosable: true
    });

    setIsProcessing(false);
    setShowConfirmModal(false);
    closeModal();
  };

  const handleStepRender = () => {
    const Component = steps[currentStep - 1];
    if (!Component) return null;

    switch (currentStep) {
      case 1:
        return (
          <FormProvider {...adjudicatorsMethods}>
            <Component
              currentStep={currentStep}
              totalSteps={totalSteps}
              goToNextStep={goToNextStep}
              goToPreviousStep={goToPreviousStep}
              closeModal={handleCloseModal}
            />
          </FormProvider>
        );
      case 2:
        return (
          <FormProvider {...adjudicatorsMethods}>
            <Component
              currentStep={currentStep}
              totalSteps={totalSteps}
              goToNextStep={goToNextStep}
              goToPreviousStep={goToPreviousStep}
              closeModal={handleCloseModal}
            />
          </FormProvider>
        );
      case 3:
        return (
          <FormProvider {...emailContentMethods}>
            <Component
              currentStep={currentStep}
              totalSteps={totalSteps}
              goToNextStep={goToNextStep}
              goToPreviousStep={goToPreviousStep}
              closeModal={handleCloseModal}
              emails={adjudicatorsMethods
                .getValues('experts')
                .flatMap(({ email, externalExpertEmail }) =>
                  [email, externalExpertEmail].filter(Boolean)
                )}
              // Pass the initial attachments from step 2
              // The rest of the syncing is done with the hook above.
              attachments={
                adjudicatorsMethods
                  .getValues('addContext')
                  .attachments?.map(file => ({
                    id: file.fileId || file.id,
                    referenceName: file.referenceName,
                    name: file.name,
                    extension: file.extension,
                    url: file.url
                  })) || []
              }
            />
          </FormProvider>
        );
      default:
        return null;
    }
  };

  return (
    <Overlay>
      <FormProvider {...adjudicatorsMethods}>
        {showConfirmModal && (
          <ConfirmInviteBoardMemberModal
            isProcessing={isProcessing}
            onClose={() => setShowConfirmModal(false)}
            onInviteExperts={handleInviteExperts}
            isAdjudicator
          />
        )}
        {showExitConfirmModal && (
          <ConfirmInviteExitModal
            onClose={handleExit}
            onCancel={() => setShowExitConfirmModal(false)}
          />
        )}
      </FormProvider>
      {handleStepRender()}
    </Overlay>
  );
};

export default InviteAdjudicatorsModal;
